import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Edit2, Trash2, FileText, LayoutTemplate, X, Maximize2, Minimize2, UploadCloud, Mail, Copy, Archive } from 'lucide-react';
import { useConfirm } from '../contexts/ConfirmContext';
import api from '../api/api';
import { cn } from '../lib/utils';
import DataTable from './ui/DataTable';
import TemplateStyleEmailEditorShell from './TemplateStyleEmailEditorShell';
import { toast } from 'sonner';
import { useTour } from './ui/TourManager';
import { templatesTourSteps } from './ui/tourSteps';

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form State
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState([]);

  // Editor State
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(true);

  const [isUploading, setIsUploading] = useState(false);
  const showPreviewPane = true;

  const { startTour } = useTour();

  // Auto-start tour on first visit
// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tourShown = localStorage.getItem('templatesTourShown');
    if (tourShown !== 'true') {
      startTour('templates', templatesTourSteps);
    }
  }, [startTour]);

  // Preview Debounce
  const previewTimeoutRef = useRef(null);

  // Pagination & Filtering for Templates
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('All');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/templates', {
        params: {
          page,
          limit,
          search: searchTerm,
          sortBy,
          sortOrder,
          activeOnly: statusFilter === 'Active' ? 'true' : statusFilter === 'Archived' ? 'false' : undefined,
          status: statusFilter === 'Archived' ? 'ARCHIVED' : undefined
        }
      });

      let fetchedData = res.data.data || [];
      if (statusFilter === 'Archived') {
        fetchedData = fetchedData.filter(t => !t.isActive);
      } else if (statusFilter === 'Active') {
        fetchedData = fetchedData.filter(t => t.isActive);
      }

      setTemplates(fetchedData);
      setTotalPages(res.data.pagination?.pages || 1);
      setTotalRecords(res.data.pagination?.total || fetchedData.length || 0);
    } catch (err) {
      console.error('Failed to load templates', err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, sortBy, sortOrder, statusFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setSelectedTemplate(null);
    setName('');
    setSubject('');
    setBodyHtml('');
    setAttachments([]);
    setPreviewData(null);
    setIsModalOpen(true);
    setIsFullscreen(true);
  };

  const openEditModal = (tpl) => {
    setIsEditing(true);
    setSelectedTemplate(tpl);
    setName(tpl.name);
    setSubject(tpl.subject);
    setBodyHtml(tpl.bodyHtml);
    setAttachments(tpl.attachments || []);
    setPreviewData(null);
    setIsModalOpen(true);
    setIsFullscreen(true);
    setTimeout(() => handlePreviewRefresh(tpl.subject, tpl.bodyHtml), 100);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !bodyHtml.trim()) {
      toast.warning("Name, Subject, and Body are required.");
      return;
    }

    try {
      if (isEditing) {
        await api.put(`/api/templates/${selectedTemplate._id}`, { name, subject, bodyHtml, attachments });
      } else {
        await api.post('/api/templates', { name, subject, bodyHtml, attachments });
      }
      setIsModalOpen(false);
      fetchTemplates();
      toast.success("Template saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save template');
    }
  };

    const confirm = useConfirm();

    const handleDelete = async (id) => {
        const ok = await confirm("Archive Template?", "Are you sure you want to archive this template?");
        if (!ok) return;
    try {
      await api.delete(`/api/templates/${id}`);
      fetchTemplates();
      toast.success("Template archived");
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete template');
    }
  };

  const handleClone = async (id) => {
    try {
      await api.post(`/api/templates/${id}/clone`);
      toast.success("Blueprint cloned");
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clone template');
    }
  };

const handlePreviewRefresh = useCallback(async (subjOverride = subject, bodyOverride = bodyHtml) => {
    setPreviewLoading(true);
    try {
      const res = await api.post('/api/templates/preview', {
        subject: subjOverride,
        bodyHtml: bodyOverride,
        attachments: attachments
      });
      setPreviewData(res.data);
    } catch (err) {
      console.error('Failed to preview', err);
    } finally {
      setPreviewLoading(false);
    }
  }, [subject, bodyHtml, attachments]);

  useEffect(() => {
    if (isModalOpen) {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = setTimeout(() => {
        handlePreviewRefresh();
      }, 1000);
    }
    return () => {
      if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    }
  }, [subject, bodyHtml, isModalOpen, handlePreviewRefresh]);

  const insertVariable = (variableName) => {
    const textToInsert = `{{${variableName}}}`;
    setBodyHtml(prev => prev + textToInsert);
  };

  /**
   * S3 UPLOAD WITHOUT SDK (Using Fetch and Pre-signed or Direct Request)
   * Note: The user provided S3 upload code uses the 'aws-sdk' module.
   * If it's missing, this component would crash unless we implement it manually.
   * I will implement a fallback or use the Provided User Logic if they confirm installation.
   * For now, I'll attempt to use standard fetch with the credentials approach if possible,
   * OR I will assume the user has 'aws-sdk' but 'react-quill-new' was the primary blocker.
   * 
   * TO BE SAFE: I will use a simple XMLHttpRequest to simulate the upload if AWS is not found.
   */
  const handleImageUploadToS3 = async (file) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return res.data.url;
    } catch (err) {
      console.error("S3 Hatası: ", err);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newAttachment = {
        filename: file.name,
        url: res.data.url,
        size: file.size,
        contentType: file.type || 'application/octet-stream'
      };

      setAttachments(prev => [...prev, newAttachment]);
    } catch (err) {
      console.error("Attachment Upload Error: ", err);
    } finally {
      setIsUploading(false);
      e.target.value = null; // Reset input
    }
  };

  const removeAttachment = (indexToRemove) => {
    setAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  if (loading && templates.length === 0) return <div className="p-8 flex justify-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div></div>;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-8 text-foreground bg-background">
      <div className="templates-header flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <LayoutTemplate className="w-10 h-10 text-primary" />
            Blueprint Library
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl font-medium">
            Manage your reusable email orchestrations. Custom Rich Editor with S3 Integrated support.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => startTour('templates', templatesTourSteps)}
            className="border-primary/30 text-primary hover:bg-primary/5 px-4 py-3 rounded-xl shadow-sm text-sm font-bold flex items-center transition-all border"
            title="Start Tour"
          >
            <LayoutTemplate className="w-4 h-4 mr-2" />
            Tour
          </button>
          <button
            onClick={openCreateModal}
            className="create-template-btn bg-primary text-primary-foreground px-6 py-3.5 rounded-2xl shadow-lg text-sm font-bold hover:opacity-90 flex items-center transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create New Blueprint
          </button>
        </div>
      </div>

      <div className="templates-table">
      <DataTable
        columns={[
          {
            header: 'Blueprint Name',
            accessor: 'name',
            sortable: true,
            render: (tpl) => (
              <div className="flex flex-col">
                <span className="font-bold text-sm tracking-tight text-foreground group-hover:text-primary transition-colors">{tpl.name}</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] font-bold text-muted-foreground bg-muted/50 px-1.5 rounded">v{tpl.version || 1}</span>
                  <span className="text-[11px] font-semibold text-muted-foreground">{new Date(tpl.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            )
          },
          {
            header: 'Status',
            accessor: 'isActive',
            sortable: true,
            render: (tpl) => (
              <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border ${tpl.status === 'ACTIVE' ? 'bg-success/10 text-success border-success/20' : tpl.status === 'ARCHIVED' ? 'bg-destructive/10 text-destructive border-destructive/20' : 'bg-muted text-muted-foreground border-border'
                }`}>
                {tpl.status || (tpl.isActive ? 'Active' : 'Archived')}
              </span>
            )
          },
          {
            header: 'Subject line',
            accessor: 'subject',
            sortable: true,
            render: (tpl) => (
              <div className="flex items-center text-sm font-medium text-foreground max-w-xs truncate">
                <Mail className="w-3.5 h-3.5 mr-2 text-primary/70 shrink-0" />
                <span className="truncate">{tpl.subject}</span>
              </div>
            )
          },
          {
            header: 'Used In',
            accessor: 'dependencySummary',
            sortable: false,
            render: (tpl) => {
              const dependencySummary = tpl.dependencySummary || {};
              const campaignCount = dependencySummary.campaignCount || 0;
              const sequenceCount = dependencySummary.sequenceCount || 0;
              const workflowCount = dependencySummary.workflowCount || 0;
              const names = [
                ...(dependencySummary.campaignNames || []),
                ...(dependencySummary.sequenceNames || []),
                ...(dependencySummary.workflowNames || [])
              ].slice(0, 3);

              if (!campaignCount && !sequenceCount && !workflowCount) {
                return <span className="text-sm text-muted-foreground italic font-medium">Unused</span>;
              }

              return (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
                    <span className="px-2 py-1 rounded-lg border bg-primary/10 text-primary border-primary/20">
                      {campaignCount} Linked Campaign{campaignCount === 1 ? '' : 's'}
                    </span>
                    <span className="px-2 py-1 rounded-lg border bg-indigo-500/10 text-indigo-600 border-indigo-500/20">
                      {sequenceCount} Copied Sequence{sequenceCount === 1 ? '' : 's'}
                    </span>
                    <span className="px-2 py-1 rounded-lg border bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                      {workflowCount} Copied Workflow{workflowCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  {names.length > 0 && (
                    <div className="text-[11px] text-muted-foreground font-medium truncate max-w-[220px]">
                      {names.join(', ')}
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            header: 'Attachments',
            accessor: 'attachments',
            sortable: false,
            render: (tpl) => tpl.attachments && tpl.attachments.length > 0 ? (
              <span className="bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border border-primary/20 flex items-center shadow-sm w-fit">
                <UploadCloud className="w-3.5 h-3.5 mr-1.5" /> {tpl.attachments.length} files
              </span>
            ) : (
              <span className="text-sm text-muted-foreground italic font-medium">None</span>
            )
          },
          {
            header: '',
            accessor: '_id',
            sortable: false,
            align: 'right',
            render: (tpl) => (
              <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleClone(tpl._id)} className="bg-primary/5 hover:bg-primary hover:text-white text-primary p-2.5 rounded-xl transition-all border border-primary/10 shadow-sm active:scale-95" title="Clone">
                  <Copy className="w-4 h-4" />
                </button>
                <button onClick={() => openEditModal(tpl)} className="bg-primary/5 hover:bg-primary hover:text-white text-primary p-2.5 rounded-xl transition-all border border-primary/10 shadow-sm active:scale-95">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(tpl._id)} className="bg-destructive/5 hover:bg-destructive hover:text-white text-destructive p-2.5 rounded-xl transition-all border border-destructive/10 shadow-sm active:scale-95">
                  {tpl.status === 'ARCHIVED' ? <Trash2 className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                </button>
              </div>
            )
          }
        ]}
        data={templates}
        loading={loading}
        pagination={{ page, limit, totalRecords, totalPages }}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        sort={{ sortBy, sortOrder }}
        onSort={handleSort}
        search={{
          placeholder: "Search blueprints...",
          value: searchTerm,
          onChange: (val) => { setSearchTerm(val); setPage(1); }
        }}
        filter={{
          value: statusFilter,
          onChange: (val) => { setStatusFilter(val); setPage(1); },
          options: [
            { label: 'All Templates', value: 'All' },
            { label: 'Active', value: 'Active', icon: <span className="w-2 h-2 rounded-full bg-success mr-2"></span> },
            { label: 'Archived', value: 'Archived', icon: <span className="w-2 h-2 rounded-full bg-muted-foreground mr-2"></span> }
          ]
        }}
        emptyState={
          <div className="text-center py-20 bg-card rounded-[2rem] border-2 border-dashed border-border shadow-sm flex flex-col items-center justify-center transition-all bg-muted/5 hover:bg-muted/10">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-6">
              <LayoutTemplate className="w-10 h-10 text-muted-foreground opacity-50" />
            </div>
            <h3 className="text-xl font-black tracking-tight text-foreground mb-2">No blueprints found</h3>
            <p className="text-sm font-medium text-muted-foreground max-w-sm mb-6">Create your first reusable design to speed up your campaigns.</p>
            <button onClick={openCreateModal} className="font-bold text-sm text-primary hover:underline flex items-center gap-1">
              Start a Blueprint <span aria-hidden="true" className="ml-1 text-lg leading-none">&rarr;</span>
            </button>
          </div>
        }
      />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-2xl flex justify-center items-center z-50 p-2 sm:p-4 md:p-6 animate-in fade-in zoom-in-95">
          <div className={cn(
            "bg-card text-card-foreground rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden transition-all duration-500 border border-border/50",
            isFullscreen ? 'w-full h-full' : 'w-full max-w-6xl h-[85vh]'
          )}>
            <TemplateStyleEmailEditorShell
              className="w-full"
              title={isEditing ? 'Refining Blueprint' : 'New Blueprint'}
              subtitle="Native Rich Editor Implementation"
              leadIcon={LayoutTemplate}
              headerActions={
                <>
                  <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-2.5 text-muted-foreground hover:text-foreground rounded-xl transition-all hidden sm:block">
                    {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                  </button>
                  <button onClick={closeModal} className="p-2.5 text-muted-foreground hover:text-destructive rounded-xl transition-all">
                    <X className="w-5 h-5" />
                  </button>
                </>
              }
              metaSection={
                <div>
                  <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Blueprint Identity</label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Welcome sequence" className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary py-3.5 px-5 transition-all" />
                </div>
              }
              subject={subject}
              onSubjectChange={setSubject}
              subjectPlaceholder="Quick question..."
              variableNames={['firstName', 'lastName', 'companyName']}
              onInsertVariable={insertVariable}
              bodyHtml={bodyHtml}
              onBodyChange={setBodyHtml}
              onImageUpload={handleImageUploadToS3}
              isUploading={isUploading}
              attachments={attachments}
              onAttachmentUpload={handleFileUpload}
              onAttachmentRemove={removeAttachment}
              previewData={previewData}
              previewLoading={previewLoading}
              showPreviewPane={showPreviewPane}
              onDiscard={closeModal}
              onSave={handleSave}
              discardLabel="Discard"
              saveLabel="Commit Blueprint"
              saveIcon={FileText}
            />
          </div>
        </div>
      )}
    </div>
  );
}
